import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, RefreshCw, ExternalLink, Search } from "lucide-react";
import AdminWorldChangerNominations from "@/components/admin/AdminWorldChangerNominations";

export default function AdminWorldChangers() {
  const [items, setItems] = useState([]);
  const [aiStatus, setAiStatus] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [busyId, setBusyId] = useState(null);

  const loadStatus = async () => {
    try {
      const { data } = await http.get("/admin/world-changers/ai-bios/status");
      setAiStatus(data);
    } catch {}
  };

  const loadItems = async () => {
    try {
      const { data } = await http.get("/world-changers", { params: { limit: 500 } });
      setItems(data);
    } catch { toast.error("Failed to load World Changers"); }
  };

  useEffect(() => { loadItems(); loadStatus(); }, []);

  useEffect(() => {
    if (!regenerating) return;
    const iv = setInterval(() => { loadStatus(); loadItems(); }, 5000);
    return () => clearInterval(iv);
  }, [regenerating]);

  useEffect(() => {
    if (regenerating && aiStatus && aiStatus.missing === 0) {
      setRegenerating(false);
      toast.success("Backfill complete");
    }
  }, [aiStatus, regenerating]);

  const regenerate = async (mode) => {
    const msg = mode === "all"
      ? `Regenerate AI biographies for ALL ${aiStatus?.total || "?"} World Changers? Uses Claude Sonnet 4.5.`
      : `Generate biographies for ${aiStatus?.missing || "?"} profiles missing one? Runs in the background.`;
    if (!window.confirm(msg)) return;
    setRegenerating(true);
    try {
      await http.post(`/admin/world-changers/ai-bios/regenerate?mode=${mode}`);
      toast.success("Queued — polling every 5s");
      setTimeout(() => setRegenerating(false), 30 * 60 * 1000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
      setRegenerating(false);
    }
  };

  const regenerateOne = async (c) => {
    if (!window.confirm(`Regenerate bio for "${c.name}"?\n\nRuns in the background — refresh in ~30s.`)) return;
    setBusyId(c.id);
    try {
      await http.post(`/admin/world-changers/${c.slug || c.id}/ai-bio`);
      toast.success("Queued — check back in ~30s");
      setTimeout(() => { loadItems(); loadStatus(); }, 45000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusyId(null); }
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((c) => {
      if (filter === "with_bio" && !c.ai_bio) return false;
      if (filter === "missing_bio" && c.ai_bio) return false;
      if (!needle) return true;
      return c.name.toLowerCase().includes(needle) || c.nationality?.toLowerCase().includes(needle) || c.occupation?.toLowerCase().includes(needle);
    });
  }, [items, filter, q]);

  const pct = aiStatus && aiStatus.total
    ? Math.round((aiStatus.with_bio / aiStatus.total) * 100) : 0;

  return (
    <div data-testid="admin-world-changers">
      <div className="mb-5 p-4 border rounded-lg flex flex-wrap items-center gap-3 justify-between" style={{ borderColor: "var(--wofy-line)", background: "rgba(196,127,58,0.08)" }}>
        <div className="text-sm min-w-0">
          <div className="font-semibold flex items-center gap-2">
            <Sparkles size={14} style={{ color: "#c47f3a" }} /> AI biographies (Claude Sonnet 4.5)
          </div>
          {aiStatus ? (
            <>
              <div className="text-xs mt-1" style={{ color: "var(--wofy-text-soft)" }}>
                {aiStatus.with_bio} of {aiStatus.total} World Changers have an AI biography · {aiStatus.missing} still missing
              </div>
              <div className="mt-2 h-1.5 w-full rounded-full overflow-hidden" style={{ background: "rgba(0,0,0,0.06)" }}>
                <div className="h-full" style={{ width: `${pct}%`, background: "#c47f3a", transition: "width 0.4s ease" }} />
              </div>
            </>
          ) : <div className="text-xs mt-1" style={{ color: "var(--wofy-text-soft)" }}>Loading status…</div>}
        </div>
        <div className="flex gap-2">
          <button
            data-testid="admin-wc-regen-missing"
            onClick={() => regenerate("missing")}
            disabled={regenerating || (aiStatus && aiStatus.missing === 0)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded font-semibold text-sm border"
            style={{ borderColor: "var(--wofy-line)", background: (regenerating || (aiStatus && aiStatus.missing === 0)) ? "#eee" : "#c47f3a", color: (regenerating || (aiStatus && aiStatus.missing === 0)) ? "#888" : "white" }}
          >
            <RefreshCw size={13} className={regenerating ? "animate-spin" : ""} />
            {regenerating ? "Running…" : `Generate for missing${aiStatus ? ` (${aiStatus.missing})` : ""}`}
          </button>
          <button
            data-testid="admin-wc-regen-all"
            onClick={() => regenerate("all")}
            disabled={regenerating}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded font-semibold text-sm border"
            style={{ borderColor: "var(--wofy-line)" }}
          >
            <Sparkles size={13} /> Regenerate ALL
          </button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 opacity-50" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, nationality…" className="w-full pl-9 pr-3 py-1.5 border rounded text-sm bg-transparent" style={{ borderColor: "var(--wofy-line)" }} />
        </div>
        {[{v:"all",l:`All (${items.length})`}, {v:"missing_bio",l:`Missing (${aiStatus?.missing ?? "?"})`}, {v:"with_bio",l:`With bio (${aiStatus?.with_bio ?? "?"})`}].map((f) => (
          <button key={f.v} onClick={() => setFilter(f.v)} className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded border" style={filter === f.v ? { borderColor: "var(--wofy-primary)", background: "var(--wofy-primary)", color: "white" } : { borderColor: "var(--wofy-line)", color: "var(--wofy-text-soft)" }}>{f.l}</button>
        ))}
      </div>

      <div className="border rounded-lg overflow-hidden" style={{ borderColor: "var(--wofy-line)" }}>
        <table className="w-full text-sm">
          <thead style={{ background: "var(--wofy-surface)" }}>
            <tr className="text-left text-[11px] font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Nationality</th>
              <th className="px-3 py-2">Biography</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} className="border-t" style={{ borderColor: "var(--wofy-line)" }}>
                <td className="px-3 py-2">
                  <div className="font-medium">{c.name}</div>
                  <div className="text-[11px] font-mono opacity-60">{c.occupation}</div>
                </td>
                <td className="px-3 py-2 text-xs" style={{ color: "var(--wofy-text-soft)" }}>{c.nationality || "—"}</td>
                <td className="px-3 py-2">
                  {c.ai_bio ? (
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "rgba(34,139,34,0.12)", color: "green" }}>✓ {c.ai_bio.word_count || 0}w</span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "rgba(196,127,58,0.15)", color: "#c47f3a" }}>Missing</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button onClick={() => regenerateOne(c)} disabled={busyId === c.id} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border mr-2" style={{ borderColor: "var(--wofy-line)" }}>
                    <RefreshCw size={11} className={busyId === c.id ? "animate-spin" : ""} />
                    {busyId === c.id ? "…" : (c.ai_bio ? "Regen" : "Generate")}
                  </button>
                  <Link to={`/world-changers/${c.slug}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border" style={{ borderColor: "var(--wofy-line)" }}>
                    View <ExternalLink size={11} />
                  </Link>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={4} className="px-3 py-8 text-center text-sm opacity-60">No matches.</td></tr>}
          </tbody>
        </table>
      </div>

      <AdminWorldChangerNominations />
    </div>
  );
}
